import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../hooks/useApi';
import ConfirmDeleteModal from '../components/ConfirmDeleteModal';
import DateInput from '../components/DateInput';
import { SectionProduitFini } from '../components/TracabiliteSections';
import EtapeTracabilite from '../components/EtapeTracabilite';
import BatchAnalytiquePanel from '../components/BatchAnalytiquePanel';
import CoutRevient from '../components/CoutRevient';
import BlocPrixVente from '../components/BlocPrixVente';
import Icon from '../components/Icon';
import { creerFormulaireTraiteFinal } from '../utils/tracabilite';
import './ProductionSaumon.css';

// ─── CONSTANTES ──────────────────────────────
const TYPES = ['Head', 'Tail', 'Body'];
const TYPE_LABELS = { Head: 'Tête', Tail: 'Queue', Body: 'Corps' };
const TYPE_COLORS = { Head: 'var(--accent)', Tail: 'var(--info)', Body: 'var(--success)' };

const ETAPES = [
  { id: 1, label: 'Batch' },
  { id: 2, label: 'Traçabilité' },
  { id: 3, label: 'Saumure' },
  { id: 4, label: 'Saisie' },
  { id: 5, label: 'Groupes' },
  { id: 6, label: 'Finaliser' },
];

// ─── SOUS-COMPOSANTS ─────────────────────────

function Stepper({ etape, nbTracabiliteNonLies = 0, onStepClick, batch, isFinalise }) {
  const peutCliquer = (cible) => {
    if (!batch && cible > 1) return false;
    if (cible < etape) return true;
    if (isFinalise) return true;
    if (cible <= etape) return true;
    return etape >= cible - 1;
  };

  return (
    <div className="stepper">
      {ETAPES.map((e, i) => {
        const clickable = onStepClick && peutCliquer(e.id);
        return (
          <React.Fragment key={e.id}>
            <div
              className={`step ${etape === e.id ? 'active' : etape > e.id ? 'done' : ''} ${clickable ? 'step-cliquable' : ''}`}
              style={{ position: 'relative', cursor: clickable ? 'pointer' : 'default' }}
              onClick={() => clickable && onStepClick(e.id)}
              title={clickable ? `Aller à l'étape ${e.id} — ${e.label}` : ''}
            >
              <div className="step-circle">
                {etape > e.id ? <Icon name="check" size={14} weight="bold" /> : e.id}
              </div>
              {e.id === 2 && nbTracabiliteNonLies > 0 && (
                <span className="step-badge-warning">{nbTracabiliteNonLies} !</span>
              )}
              <span className="step-label">{e.label}</span>
            </div>
            {i < ETAPES.length - 1 && (
              <div className={`step-line ${etape > e.id ? 'done' : ''}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function StatBadge({ label, value, color }) {
  return (
    <div className="stat-badge" style={{ borderColor: color }}>
      <span className="stat-value" style={{ color }}>{value}</span>
      <span className="stat-label">{label}</span>
    </div>
  );
}

// ─── ÉTAPE 1 : BATCH ─────────────────────────

function EtapeBatch({ onBatchCree }) {
  const [batches, setBatches] = useState([]);
  const [mode, setMode] = useState('liste'); // 'liste' | 'nouveau'
  const [form, setForm] = useState({
    nom: '',
    date_prod: new Date().toISOString().slice(0, 10),
    operateurs: '',
    fournisseur: '',
    prix_kg: '',
    poids_cru_kg: '',
    nb_filets: '',
    peau: 'avec',
    notes_qualite_filets: '',
    notes: '',
    mode_prix_vente: 'kg',
    prix_vente_kg: '',
    prix_vente_unite: '',
  });
  const [nomAuto, setNomAuto] = useState('');
  const [nomManuel, setNomManuel] = useState(false);
  const [loading, setLoading] = useState(false);
  const [erreur, setErreur] = useState('');
  const [batchASupprimer, setBatchASupprimer] = useState(null);
  const [suppLoading, setSuppLoading] = useState(false);
  const [toast, setToast] = useState('');
  const [batchAnalytique, setBatchAnalytique] = useState(null);

  useEffect(() => {
    api.get('/saumon/batches').then(setBatches).catch(() => {});
  }, []);

  useEffect(() => {
    if (mode === 'nouveau' && !nomManuel) {
      api.get('/batches/prochain-numero/saumon')
        .then(r => { setNomAuto(r.nom); setForm(f => ({ ...f, nom: r.nom })); })
        .catch(() => {});
    }
  }, [mode]); // eslint-disable-line

  const afficherToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3500);
  };

  const supprimerBatch = async () => {
    if (!batchASupprimer) return;
    setSuppLoading(true);
    try {
      await api.delete(`/saumon/batches/${batchASupprimer.id}`);
      setBatches(prev => prev.filter(b => b.id !== batchASupprimer.id));
      afficherToast(`Batch ${batchASupprimer.nom} supprimé`);
    } catch (e) {
      setErreur(e.response?.data?.error || 'Erreur lors de la suppression');
    } finally {
      setSuppLoading(false);
      setBatchASupprimer(null);
    }
  };

  const creerBatch = async () => {
    if (!form.date_prod) {
      setErreur('Date requise');
      return;
    }
    if (!form.operateurs) {
      setErreur('Veuillez sélectionner les opérateurs');
      return;
    }
    setLoading(true);
    setErreur('');
    try {
      const result = await api.post('/saumon/batches', {
        ...form,
        nom           : form.nom || undefined,
        prix_kg:          parseFloat(form.prix_kg) || 0,
        poids_cru_kg:     parseFloat(form.poids_cru_kg) || 0,
        nb_filets:        parseInt(form.nb_filets) || 0,
        prix_vente_kg:    parseFloat(form.prix_vente_kg) || null,
        prix_vente_unite: parseFloat(form.prix_vente_unite) || null,
        mode_prix_vente:  form.mode_prix_vente || 'kg',
      });
      const batch = await api.get(`/saumon/batches/${result.id}`);
      onBatchCree(batch);
    } catch (e) {
      setErreur(e.response?.data?.error || 'Erreur création batch');
    } finally {
      setLoading(false);
    }
  };

  const ouvrirBatch = async (id) => {
    try {
      const batch = await api.get(`/saumon/batches/${id}`);
      onBatchCree(batch);
    } catch (e) {
      setErreur('Impossible de charger ce batch');
    }
  };

  return (
    <div className="etape-content">
      {batchASupprimer && (
        <ConfirmDeleteModal
          nomBatch={batchASupprimer.nom}
          loading={suppLoading}
          onConfirm={supprimerBatch}
          onAnnuler={() => setBatchASupprimer(null)}
        />
      )}
      {batchAnalytique && (
        <div className="modal-overlay" onClick={() => setBatchAnalytique(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 860, maxHeight: '90vh', overflowY: 'auto' }}>
            <BatchAnalytiquePanel batch={batchAnalytique} onFermer={() => setBatchAnalytique(null)} />
          </div>
        </div>
      )}
      {toast && <div className="toast-success">{toast}</div>}

      <div className="etape-header">
        <h2>Batch de production</h2>
        <p className="muted">Crée un nouveau batch ou reprends un batch existant.</p>
      </div>

      <div className="batch-toggle">
        <button
          className={`btn ${mode === 'nouveau' ? 'btn-primary' : 'btn-ghost'}`}
          onClick={() => setMode('nouveau')}
        >
          + Nouveau batch
        </button>
        <button
          className={`btn ${mode === 'liste' ? 'btn-primary' : 'btn-ghost'}`}
          onClick={() => setMode('liste')}
        >
          Reprendre un batch
        </button>
      </div>

      {mode === 'nouveau' && (
        <div className="card form-card">
          <div className="form-grid">
            <div className="form-field">
              <label>Numéro du batch</label>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input
                  value={form.nom}
                  onChange={e => setForm({ ...form, nom: e.target.value })}
                  readOnly={!nomManuel}
                  style={{ flex: 1, opacity: nomManuel ? 1 : 0.75, background: nomManuel ? undefined : 'rgba(255,255,255,0.04)' }}
                  placeholder={nomAuto || 'Chargement…'}
                />
                <button type="button" className="btn btn-ghost btn-sm"
                  onClick={() => setNomManuel(m => !m)} style={{ whiteSpace: 'nowrap' }}>
                  {nomManuel ? '↩ Auto' : '✎ Modifier'}
                </button>
              </div>
              <span className="muted" style={{ fontSize: 12 }}>Numéro auto-généré</span>
            </div>
            <div className="form-field">
              <label>Date de production *</label>
              <DateInput
                value={form.date_prod}
                onChange={v => setForm({ ...form, date_prod: v })}
              />
            </div>
            <div className="form-field">
              <label>Opérateurs *</label>
              <select
                value={form.operateurs}
                onChange={e => setForm({ ...form, operateurs: e.target.value })}
                required
              >
                <option value="">— Sélectionner —</option>
                <option value="LCU">LCU</option>
                <option value="ELA">ELA</option>
                <option value="LCU+ELA">Les deux</option>
              </select>
            </div>
            <div className="form-field">
              <label>Fournisseur</label>
              <input
                value={form.fournisseur}
                onChange={e => setForm({ ...form, fournisseur: e.target.value })}
                placeholder="ex: Viandex"
              />
            </div>
            <div className="form-field">
              <label>Prix d'achat matière (cru) — $/kg</label>
              <input
                type="number"
                step="0.01"
                value={form.prix_kg}
                onChange={e => setForm({ ...form, prix_kg: e.target.value })}
                placeholder="ex: 18.50"
              />
            </div>
            <div className="form-field">
              <label>Poids total (kg)</label>
              <input
                type="number"
                step="0.1"
                value={form.poids_cru_kg}
                onChange={e => setForm({ ...form, poids_cru_kg: e.target.value })}
                placeholder="ex: 20"
              />
            </div>
            <div className="form-field">
              <label>Nombre de filets</label>
              <input
                type="number"
                step="1"
                min="0"
                value={form.nb_filets}
                onChange={e => setForm({ ...form, nb_filets: e.target.value })}
                placeholder="ex: 4"
              />
            </div>
          </div>

          <div className="form-field">
            <label>Peau</label>
            <div className="radio-group">
              {['avec', 'sans', 'mixte'].map(p => (
                <label key={p} className={`radio-option ${form.peau === p ? 'active' : ''}`}>
                  <input
                    type="radio"
                    name="peau"
                    value={p}
                    checked={form.peau === p}
                    onChange={() => setForm({ ...form, peau: p })}
                  />
                  {p.charAt(0).toUpperCase() + p.slice(1)}
                </label>
              ))}
            </div>
          </div>

          <div className="form-field">
            <label>Notes qualité filets</label>
            <textarea
              value={form.notes_qualite_filets}
              onChange={e => setForm({ ...form, notes_qualite_filets: e.target.value })}
              placeholder="ex: Chair pâle, moins belle — fournisseur X"
              rows={2}
            />
          </div>

          {/* BLOC VENTE saumon — mode par défaut batch (hérité par les groupes) */}
          <BlocPrixVente
            mode={form.mode_prix_vente}
            prixKg={form.prix_vente_kg}
            prixUnite={form.prix_vente_unite}
            onModeChange={m => setForm({ ...form, mode_prix_vente: m })}
            onPrixKgChange={v => setForm({ ...form, prix_vente_kg: v })}
            onPrixUniteChange={v => setForm({ ...form, prix_vente_unite: v })}
            hasToggle={true}
            verrouille={false}
            labelUnite="Prix de vente à l'unité ($/groupe)"
          />

          <div className="form-field" style={{ marginTop: 12 }}>
            <label>Notes</label>
            <textarea
              value={form.notes}
              onChange={e => setForm({ ...form, notes: e.target.value })}
              placeholder="Observations, qualité, etc."
              rows={2}
            />
          </div>
          {erreur && <p className="erreur-msg">{erreur}</p>}
          <button className="btn btn-primary btn-lg" onClick={creerBatch} disabled={loading || !form.operateurs}>
            {loading ? 'Création...' : 'Créer le batch →'}
          </button>
        </div>
      )}

      {mode === 'liste' && (
        <div className="card">
          {batches.length === 0
            ? <p className="muted empty-msg">Aucun batch saumon existant.</p>
            : (
              <table className="esc-table">
                <thead>
                  <tr>
                    <th>Batch</th>
                    <th>Date</th>
                    <th>Fournisseur</th>
                    <th>$/kg</th>
                    <th>Paquets</th>
                    <th>Qualité filets</th>
                    <th>Statut</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {batches.map(b => (
                    <tr key={b.id}>
                      <td className="font-medium">{b.nom}</td>
                      <td className="muted">{b.date_prod}</td>
                      <td className="muted">{b.fournisseur || '—'}</td>
                      <td className="gold">{b.prix_kg ? `${b.prix_kg}$` : '—'}</td>
                      <td>{b.nb_paquets}</td>
                      <td className="muted" style={{ maxWidth: 180, fontSize: 12 }}>
                        {b.notes_qualite_filets
                          ? <span title={b.notes_qualite_filets}>
                              {b.notes_qualite_filets.length > 40
                                ? b.notes_qualite_filets.slice(0, 40) + '…'
                                : b.notes_qualite_filets}
                            </span>
                          : '—'}
                      </td>
                      <td>
                        <span className={`badge badge-${b.statut}`}>
                          {b.statut === 'finalise' ? 'Finalisé' : 'En cours'}
                        </span>
                        {b.nb_tracabilite_manquante > 0 && (
                          <span className="badge badge--warning" style={{ marginLeft: 6 }}>
                            <Icon name="attention" size={11} /> {b.nb_tracabilite_manquante} sans facture
                          </span>
                        )}
                      </td>
                      <td className="td-actions">
                        <button className="btn btn-ghost btn-sm" onClick={() => ouvrirBatch(b.id)}>
                          Ouvrir →
                        </button>
                        {b.statut === 'finalise' && (
                          <Link className="btn btn-ghost btn-sm" to={`/batches/${b.id}/tracabilite`}>
                            Fiche de traçabilité
                          </Link>
                        )}
                        {b.statut === 'finalise' && (
                          <button className="btn btn-ghost btn-sm" onClick={() => setBatchAnalytique(b)} title="Analytique">
                            <Icon name="analytics" size={14} />
                          </button>
                        )}
                        <button
                          className="btn btn-danger btn-sm btn-icon"
                          onClick={() => setBatchASupprimer(b)}
                          title="Supprimer ce batch"
                        >
                          <Icon name="supprimer" size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          }
        </div>
      )}
    </div>
  );
}

// ─── ÉTAPE 2 : RECETTE DE SAUMURE ────────────

function formatHeure(datetimeStr) {
  if (!datetimeStr) return null;
  return datetimeStr.slice(11, 16); // 'HH:MM'
}

function nowLocalISO() {
  const d = new Date();
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function dansMinutesLabel(heureStr) {
  if (!heureStr) return '';
  const t = new Date(heureStr.replace(' ', 'T'));
  const diff = Math.round((t - new Date()) / 60000);
  if (diff <= 0) return 'maintenant';
  if (diff < 60) return `dans ${diff} min`;
  const h = Math.floor(diff / 60);
  const m = diff % 60;
  return m > 0 ? `dans ${h}h${String(m).padStart(2,'0')}` : `dans ${h}h`;
}

function TimelineItem({ icone, couleur, heure, label, sous_label, action, actionLabel }) {
  return (
    <div className="timeline-item" style={{ '--tl-color': couleur }}>
      <div className="timeline-icone">{icone}</div>
      <div className="timeline-corps">
        <div className="timeline-heure">{heure ? formatHeure(heure) : '—'}</div>
        <div className="timeline-label">{label}</div>
        {sous_label && <div className="timeline-sous-label muted">{sous_label}</div>}
        {action && (
          <button className="btn btn-ghost btn-sm" style={{ marginTop: 4 }} onClick={action}>
            {actionLabel || <><Icon name="check" size={13} weight="bold" /> Confirmer</>}
          </button>
        )}
      </div>
    </div>
  );
}

function Timeline({ timeline, onConfirmerRotation, onDebuterSechage, onDebuterFumage }) {
  if (!timeline || !timeline.debut_saumurage) return null;

  const statut = timeline.statut_actuel;
  const rot1 = timeline.rotation_1;
  const rot2 = timeline.rotation_2;

  const couleur = (actif, fait) => {
    if (fait) return 'var(--success)';
    if (actif) return 'var(--accent)';
    return 'var(--muted)';
  };
  const icone = (actif, fait) => {
    if (fait) return <Icon name="succes" size={13} />;
    if (actif) return <Icon name="timer" size={13} />;
    return <Icon name="heure" size={13} />;
  };

  const rot1Actif = statut === 'rotation_1_due';
  const rot2Actif = statut === 'rotation_2_due';
  const sechageActif = statut === 'saumurage_termine';
  const fumageActif = !!timeline.debut_sechage && !timeline.debut_fumage;

  return (
    <div className="card timeline-card">
      <h3 className="timeline-titre">Progression du saumurage</h3>
      <div className="timeline">
        <TimelineItem
          icone={<Icon name="succes" size={13} />} couleur="var(--success)"
          heure={timeline.debut_saumurage}
          label="Début saumurage"
        />
        {rot1 && (
          <TimelineItem
            icone={icone(rot1Actif, rot1.fait)}
            couleur={couleur(rot1Actif, rot1.fait)}
            heure={rot1.reelle || rot1.prevue}
            label={`Rotation 1${rot1.fait ? ` (réelle : ${formatHeure(rot1.reelle)})` : rot1Actif ? ' ← due !' : ` (prévue)`}`}
            sous_label="Flipper les filets + intervertir dessus/dessous"
            action={rot1Actif && !rot1.fait ? () => onConfirmerRotation(1) : null}
          />
        )}
        {rot2 && (
          <TimelineItem
            icone={icone(rot2Actif, rot2.fait)}
            couleur={couleur(rot2Actif, rot2.fait)}
            heure={rot2.reelle || rot2.prevue}
            label={`Rotation 2${rot2.fait ? ` (réelle : ${formatHeure(rot2.reelle)})` : rot2Actif ? ' ← due !' : ` (prévue)`}`}
            sous_label="Refaire une rotation"
            action={rot2Actif && !rot2.fait ? () => onConfirmerRotation(2) : null}
          />
        )}
        <TimelineItem
          icone={sechageActif ? <Icon name="timer" size={13} /> : timeline.debut_sechage ? <Icon name="succes" size={13} /> : <Icon name="heure" size={13} />}
          couleur={timeline.debut_sechage ? 'var(--success)' : sechageActif ? 'var(--accent)' : 'var(--muted)'}
          heure={timeline.debut_sechage || timeline.fin_saumurage}
          label={timeline.debut_sechage ? 'Séchage démarré' : 'Fin saumurage → rincer'}
          sous_label="Rincer les filets, déposer sur grille (12-24h)"
          action={sechageActif && !timeline.debut_sechage ? onDebuterSechage : null}
          actionLabel="Démarrer séchage"
        />
        <TimelineItem
          icone={timeline.debut_fumage ? <Icon name="succes" size={13} /> : fumageActif ? <Icon name="timer" size={13} /> : <Icon name="heure" size={13} />}
          couleur={timeline.debut_fumage ? 'var(--success)' : fumageActif ? 'var(--accent)' : 'var(--muted)'}
          heure={timeline.debut_fumage}
          label={timeline.debut_fumage ? `Fumage démarré (${timeline.duree_fumage_h}h cerisier)` : 'Début fumage (3-4h cerisier froid)'}
          action={fumageActif && !timeline.debut_fumage ? onDebuterFumage : null}
          actionLabel="Démarrer fumage"
        />
      </div>

      {timeline.prochaine_action && (
        <div className="timeline-prochaine" style={{ borderColor: 'var(--gold-500)' }}>
          Prochaine action : <strong>{timeline.prochaine_action.description}</strong>
          {timeline.prochaine_action.dans_minutes > 0 && (
            <span className="muted"> — {dansMinutesLabel(timeline.prochaine_action.heure)}</span>
          )}
        </div>
      )}
    </div>
  );
}

// ─── PANNEAU PLAY/STOP FUMAGE SAUMON ─────────
// Utilise PATCH /api/batches/:id/cuisson/demarrer|arreter (CCP HACCP)
// distinct des routes /saumon/batches/:id/fumage/debut qui trackent la timeline saumurage

function PanneauTimerFumageSaumon({ batchId, batch, onRefresh }) {
  const [chargement, setChargement] = useState(false);
  const [erreur, setErreur]         = useState('');
  const [tempsEcoule, setTempsEcoule] = useState('');

  useEffect(() => {
    if (!batch.heure_debut || batch.heure_fin) return;
    const calculer = () => {
      const ms = new Date() - new Date(batch.heure_debut);
      if (ms < 0) return;
      const h = Math.floor(ms / 3600000);
      const m = Math.floor((ms % 3600000) / 60000);
      setTempsEcoule(`${h}h ${String(m).padStart(2, '0')}min`);
    };
    calculer();
    const iv = setInterval(calculer, 30000);
    return () => clearInterval(iv);
  }, [batch.heure_debut, batch.heure_fin]);

  const fmtH = (iso) => iso ? new Date(iso).toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit' }) : '—';
  const duree = (d, f) => {
    if (!d || !f) return '';
    const ms = new Date(f) - new Date(d);
    if (ms < 0) return '';
    return `${Math.floor(ms / 3600000)}h ${String(Math.floor((ms % 3600000) / 60000)).padStart(2, '0')}min`;
  };

  const action = async (type) => {
    setChargement(true); setErreur('');
    try {
      await api.patch(`/batches/${batchId}/cuisson/${type}`);
      onRefresh();
    } catch (e) { setErreur(e.response?.data?.error || 'Erreur'); }
    finally { setChargement(false); }
  };

  return (
    <div className="card timer-cuisson-panel" style={{ marginBottom: 16 }}>
      <h4 style={{ marginBottom: 8 }}>Suivi cuisson (fumage — CCP HACCP)</h4>
      <p className="muted" style={{ fontSize: 12, marginBottom: 12 }}>
        Enregistre les heures de début/fin de fumage pour la traçabilité MAPAQ.
      </p>
      {erreur && <p className="danger" style={{ fontSize: 13, marginBottom: 8 }}>{erreur}</p>}
      {!batch.heure_debut && (
        <button className="btn btn-primary" onClick={() => action('demarrer')} disabled={chargement}>
          <Icon name="demarrer" size={15} />{chargement ? ' ...' : ' Démarrer le chrono fumage'}
        </button>
      )}
      {batch.heure_debut && !batch.heure_fin && (
        <div>
          <div style={{ display: 'flex', gap: 16, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            <span className="success" style={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
              <Icon name="demarrer" size={14} /> Fumage démarré à {fmtH(batch.heure_debut)}
            </span>
            {tempsEcoule && <span className="gold">{tempsEcoule} écoulées</span>}
          </div>
          <button className="btn btn-danger" onClick={() => action('arreter')} disabled={chargement}>
            <Icon name="arreter" size={15} />{chargement ? ' ...' : ' Arrêter le chrono fumage'}
          </button>
        </div>
      )}
      {batch.heure_debut && batch.heure_fin && (
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
          <span className="muted">Fumage {fmtH(batch.heure_debut)} → {fmtH(batch.heure_fin)}</span>
          <span className="gold" style={{ fontWeight: 600 }}>Durée : {duree(batch.heure_debut, batch.heure_fin)}</span>
        </div>
      )}
    </div>
  );
}

function EtapeRecette({ batch, setBatch, onSuivant }) {
  const [recette, setRecette] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [loading, setLoading] = useState(false);
  const [erreur, setErreur] = useState('');
  const [succes, setSucces] = useState('');

  const poidsKg = batch.poids_cru_kg || 0;

  const chargerRecette = () => {
    if (poidsKg > 0) {
      api.get(`/saumon/recette/calculateur?poids_kg=${poidsKg}`)
        .then(setRecette).catch(() => {});
    } else {
      api.get('/saumon/recette').then(r => setRecette({ recette_base: r })).catch(() => {});
    }
  };

  const chargerTimeline = () => {
    api.get(`/saumon/batches/${batch.id}/timeline`)
      .then(setTimeline).catch(() => {});
  };

  useEffect(() => {
    chargerRecette();
    chargerTimeline();
  }, [batch.id]);

  // Rafraîchissement auto toutes les 60s
  useEffect(() => {
    if (!timeline?.debut_saumurage) return;
    const interval = setInterval(chargerTimeline, 60000);
    return () => clearInterval(interval);
  }, [timeline?.debut_saumurage, batch.id]);

  const demarrerSaumurage = async () => {
    setLoading(true);
    setErreur('');
    try {
      const res = await api.post(`/saumon/batches/${batch.id}/saumurage/debut`, {
        debut_saumurage: nowLocalISO(),
      });
      setSucces(`Saumurage démarré — fin prévue à ${formatHeure(res.fin_saumurage)}`);
      setTimeout(() => setSucces(''), 5000);
      chargerTimeline();
      const updated = await api.get(`/saumon/batches/${batch.id}`);
      setBatch(updated);
    } catch (e) {
      setErreur(e.response?.data?.error || 'Erreur démarrage saumurage');
    } finally {
      setLoading(false);
    }
  };

  const confirmerRotation = async (numero) => {
    try {
      await api.post(`/saumon/batches/${batch.id}/saumurage/rotation/${numero}`, {
        heure_reelle: nowLocalISO(),
      });
      chargerTimeline();
    } catch (e) {
      setErreur(e.response?.data?.error || 'Erreur confirmation rotation');
    }
  };

  const demarrerSechage = async () => {
    try {
      await api.post(`/saumon/batches/${batch.id}/sechage/debut`, { debut_sechage: nowLocalISO() });
      chargerTimeline();
    } catch (e) {
      setErreur(e.response?.data?.error || 'Erreur démarrage séchage');
    }
  };

  const demarrerFumage = async () => {
    try {
      await api.post(`/saumon/batches/${batch.id}/fumage/debut`, {
        debut_fumage: nowLocalISO(),
        duree_fumage_h: 3.5,
      });
      chargerTimeline();
    } catch (e) {
      setErreur(e.response?.data?.error || 'Erreur démarrage fumage');
    }
  };

  const copierRecette = () => {
    if (!recette) return;
    const texte = [
      `Recette de saumure — ${poidsKg} kg de saumon`,
      '',
      'ÉPICES (broyer grossièrement ensemble)',
      `Anis étoilé entier  : ${recette.epices?.anis_etoile}`,
      `Baies de genièvre   : ${recette.epices?.baies_genievre}`,
      `Grains poivre noir  : ${recette.epices?.poivre_noir}`,
      '',
      'SAUMURE',
      `Cassonade           : ${recette.saumure?.cassonade}`,
      `Sel de Guérande     : ${recette.saumure?.sel_guerande}`,
    ].join('\n');
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(texte).then(() => {
        setSucces('Recette copiée dans le presse-papier');
        setTimeout(() => setSucces(''), 3000);
      });
    } else {
      // Fallback pour contextes non-sécurisés (HTTP réseau local)
      const el = document.createElement('textarea');
      el.value = texte;
      el.style.position = 'fixed';
      el.style.opacity = '0';
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setSucces('Recette copiée dans le presse-papier');
      setTimeout(() => setSucces(''), 3000);
    }
  };

  return (
    <div className="etape-content">
      <div className="etape-header">
        <h2>Recette de saumure</h2>
        <p className="muted">
          Batch : <strong className="gold">{batch.nom}</strong>
          {poidsKg > 0 && <> · {poidsKg} kg</>}
        </p>
      </div>

      {recette && recette.epices && (
        <div className="card recette-saumure">
          <div className="recette-titre">
            Recette — {poidsKg > 0 ? `${poidsKg} kg` : 'base'}
            {recette.multiplicateur && <span className="muted" style={{ marginLeft: 8, fontSize: 13 }}>×{recette.multiplicateur}</span>}
          </div>

          <div className="recette-section">
            <div className="recette-section-titre">ÉPICES <span className="muted">(broyer grossièrement ensemble)</span></div>
            <div className="recette-ligne"><span>Anis étoilé entier</span><strong>{recette.epices.anis_etoile}</strong></div>
            <div className="recette-ligne"><span>Baies de genièvre</span><strong>{recette.epices.baies_genievre}</strong></div>
            <div className="recette-ligne"><span>Grains poivre noir</span><strong>{recette.epices.poivre_noir}</strong></div>
          </div>

          <div className="recette-section">
            <div className="recette-section-titre">SAUMURE</div>
            <div className="recette-ligne"><span>Cassonade</span><strong>{recette.saumure.cassonade}</strong></div>
            <div className="recette-ligne"><span>Sel de Guérande</span><strong>{recette.saumure.sel_guerande}</strong></div>
          </div>

          <div className="recette-section">
            <div className="recette-section-titre">APPLICATION</div>
            {recette.instructions?.map((inst, i) => (
              <div key={i} className="recette-instruction muted">{inst}</div>
            ))}
          </div>

          {recette.rotations && (
            <div className="recette-section">
              <div className="recette-section-titre">ROTATIONS</div>
              {recette.rotations.map(r => (
                <div key={r.numero} className="recette-ligne">
                  <span>Rotation {r.numero} — à {r.a}</span>
                  <span className="muted">{r.action}</span>
                </div>
              ))}
            </div>
          )}

          <div className="recette-actions">
            <button className="btn btn-ghost btn-sm" onClick={copierRecette}>
              Copier la recette
            </button>
          </div>
        </div>
      )}

      {succes && <div className="succes-msg">{succes}</div>}
      {erreur && <div className="erreur-msg">{erreur}</div>}

      {!timeline?.debut_saumurage && (
        <div className="card" style={{ textAlign: 'center', padding: '20px 16px' }}>
          <p className="muted" style={{ marginBottom: 12 }}>
            Appliquer la saumure sur les filets, empiler dans un contenant, puis démarrer le chrono.
          </p>
          <button
            className="btn btn-primary btn-lg"
            onClick={demarrerSaumurage}
            disabled={loading}
          >
            {loading ? 'Démarrage...' : 'Démarrer le saumurage maintenant →'}
          </button>
        </div>
      )}

      {timeline?.debut_saumurage && (
        <Timeline
          timeline={timeline}
          onConfirmerRotation={confirmerRotation}
          onDebuterSechage={demarrerSechage}
          onDebuterFumage={demarrerFumage}
        />
      )}

      {/* Panneau play/stop cuisson HACCP — fumage uniquement (V1)
          Remarque : saumurage et séchage ont leur propre tracking via /saumon/batches/:id/saumurage|sechage.
          Seul le fumage constitue la "cuisson" au sens HACCP (CCP température).
          PATCH /batches/:id/cuisson/demarrer stocke heure_debut en ISO 8601 UTC. */}
      {timeline?.debut_fumage && (
        <PanneauTimerFumageSaumon
          batchId={batch.id}
          batch={batch}
          onRefresh={chargerTimeline}
        />
      )}

      <div className="etape-actions" style={{ marginTop: 16 }}>
        <button className="btn btn-primary" onClick={onSuivant}>
          Continuer → Saisie des paquets
        </button>
      </div>
    </div>
  );
}

// ─── ÉTAPE 3 : SAISIE ────────────────────────

function EtapeSaisie({ batch, setBatch, onSuivant }) {
  const [type, setType]     = useState('Head');
  const [poids, setPoids]   = useState('');
  const [erreur, setErreur] = useState('');
  const [succes, setSucces] = useState('');
  const [prochains, setProchains] = useState({ Head: 1, Tail: 1, Body: 1 });
  const [importLoading, setImportLoading] = useState(false);
  const [ajoutLoading, setAjoutLoading] = useState(false);
  const [trie, setTrie] = useState(false);
  const poidsRef = useRef(null);
  const importRef = useRef(null);

  useEffect(() => {
    api.get(`/saumon/batches/${batch.id}/prochains-numeros`)
      .then(setProchains).catch(() => {});
    poidsRef.current?.focus();
  }, [batch.id]);

  const ajouterPaquet = async () => {
    const p = parseFloat(poids);
    if (!poids || isNaN(p) || p <= 0) {
      setErreur('Entrez un poids valide');
      return;
    }
    // Vider immédiatement pour bloquer un 2e appui sur Entrée
    setPoids('');
    setErreur('');
    setAjoutLoading(true);
    try {
      const result = await api.post(`/saumon/batches/${batch.id}/paquets`, {
        type_pack: type,
        poids_g: p,
      });
      setProchains(result.prochains);
      setSucces(`${type} ${result.numero} — ${p}g ajouté`);
      setTimeout(() => setSucces(''), 2500);

      // Rafraîchir les paquets du batch
      const updated = await api.get(`/saumon/batches/${batch.id}`);
      setBatch(updated);
      poidsRef.current?.focus();
    } catch (e) {
      setErreur(e.response?.data?.error || 'Erreur ajout paquet');
    } finally {
      setAjoutLoading(false);
    }
  };

  const supprimerPaquet = async (id) => {
    try {
      await api.delete(`/saumon/paquets/${id}`);
      const updated = await api.get(`/saumon/batches/${batch.id}`);
      setBatch(updated);
      const np = await api.get(`/saumon/batches/${batch.id}/prochains-numeros`);
      setProchains(np);
    } catch (e) {
      setErreur('Erreur suppression');
    }
  };

  const exporterCSV = () => {
    const paquets = batch.paquets || [];
    const lignes = ['type_pack,poids_g', ...paquets.map(p => `${p.type_pack},${p.poids_g}`)];
    const blob = new Blob([lignes.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `paquets-${batch.nom.replace(/\s+/g, '-')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const importerCSV = async (e) => {
    const fichier = e.target.files[0];
    if (!fichier) return;
    e.target.value = '';

    const texte = await fichier.text();
    const lignes = texte.trim().split('\n').filter(Boolean);

    // Ignorer la ligne d'en-tête si présente
    const debut = lignes[0].toLowerCase().startsWith('type') ? 1 : 0;
    const paquets = lignes.slice(debut).map(l => {
      const [type_pack, poids_g] = l.split(',').map(s => s.trim());
      return { type_pack, poids_g };
    });

    if (paquets.length === 0) {
      setErreur('Fichier CSV vide ou invalide');
      return;
    }

    setImportLoading(true);
    setErreur('');
    try {
      const result = await api.post(`/saumon/batches/${batch.id}/paquets/import`, { paquets });
      setProchains(result.prochains);

      if (result.erreurs.length > 0) {
        setErreur(`${result.importes} importé(s), ${result.erreurs.length} erreur(s) : ${result.erreurs[0]}`);
      } else {
        setSucces(`${result.importes} paquet(s) importé(s)`);
        setTimeout(() => setSucces(''), 3000);
      }

      const updated = await api.get(`/saumon/batches/${batch.id}`);
      setBatch(updated);
    } catch (e) {
      setErreur(e.response?.data?.error || 'Erreur import CSV');
    } finally {
      setImportLoading(false);
    }
  };

  const viderTout = async () => {
    if (!window.confirm(`Supprimer tous les paquets de ce batch ? Cette action est irréversible.`)) return;
    try {
      await api.delete(`/saumon/batches/${batch.id}/paquets`);
      const updated = await api.get(`/saumon/batches/${batch.id}`);
      setBatch(updated);
      const np = await api.get(`/saumon/batches/${batch.id}/prochains-numeros`);
      setProchains(np);
      setSucces('Tous les paquets ont été supprimés.');
      setTimeout(() => setSucces(''), 3000);
    } catch (e) {
      setErreur('Erreur lors de la suppression');
    }
  };

  const trierPaquets = () => {
    setTrie(t => !t);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !ajoutLoading) ajouterPaquet();
    if (e.key === 'h' || e.key === 'H') { setType('Head'); e.preventDefault(); }
    if (e.key === 't' || e.key === 'T') { setType('Tail'); e.preventDefault(); }
    if (e.key === 'b' || e.key === 'B') { setType('Body'); e.preventDefault(); }
  };

  const TYPE_ORDER = { Head: 0, Tail: 1, Body: 2 };
  const paquets = batch.paquets || [];
  const paquetsAffiches = trie
    ? [...paquets].sort((a, b) => {
        const tOrd = TYPE_ORDER[a.type_pack] - TYPE_ORDER[b.type_pack];
        if (tOrd !== 0) return tOrd;
        return parseInt(a.numero.replace(/\D/g, ''), 10) - parseInt(b.numero.replace(/\D/g, ''), 10);
      })
    : [...paquets].reverse();
  const stats = {
    Head: paquets.filter(p => p.type_pack === 'Head').length,
    Tail: paquets.filter(p => p.type_pack === 'Tail').length,
    Body: paquets.filter(p => p.type_pack === 'Body').length,
  };

  return (
    <div className="etape-content">
      <div className="etape-header">
        <h2>Saisie des paquets</h2>
        <p className="muted">
          Batch : <strong className="gold">{batch.nom}</strong>
          {batch.prix_kg > 0 && <> · {batch.prix_kg}$/kg</>}
        </p>
      </div>

      {/* Raccourcis clavier + CSV */}
      <div className="raccourcis card">
        <span className="muted" style={{ fontSize: 12 }}>Raccourcis clavier :</span>
        <kbd>H</kbd><span className="muted"> Tête</span>
        <kbd>T</kbd><span className="muted"> Queue</span>
        <kbd>B</kbd><span className="muted"> Corps</span>
        <kbd>↵</kbd><span className="muted"> Ajouter</span>
        <div className="csv-actions">
          <button
            className="btn btn-ghost btn-sm"
            onClick={trierPaquets}
            disabled={(batch.paquets || []).length === 0}
            title="Trier : Tête → Queue → Corps, puis par numéro"
          >
            {trie ? '⇅ Récent en tête' : '⇅ Trier par type'}
          </button>
          <button
            className="btn btn-ghost btn-sm"
            onClick={exporterCSV}
            disabled={(batch.paquets || []).length === 0}
            title="Exporter les paquets en CSV"
          >
            ↓ Exporter CSV
          </button>
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => importRef.current?.click()}
            disabled={importLoading}
            title="Importer des paquets depuis un CSV (colonnes : type_pack, poids_g)"
          >
            {importLoading ? 'Import...' : '↑ Importer CSV'}
          </button>
          <button
            className="btn btn-danger btn-sm"
            onClick={viderTout}
            disabled={(batch.paquets || []).length === 0}
            title="Supprimer tous les paquets de ce batch"
          >
            <Icon name="supprimer" size={14} /> Vider tout
          </button>
          <input
            ref={importRef}
            type="file"
            accept=".csv"
            style={{ display: 'none' }}
            onChange={importerCSV}
          />
        </div>
      </div>

      {/* Formulaire d'entrée */}
      <div className="card saisie-form">
        <div className="type-selector">
          {TYPES.map(t => (
            <button
              key={t}
              className={`type-btn ${type === t ? 'active' : ''}`}
              style={{ '--type-color': TYPE_COLORS[t] }}
              onClick={() => { setType(t); poidsRef.current?.focus(); }}
            >
              {TYPE_LABELS[t]}
              <span className="type-next">→ #{prochains[t]}</span>
            </button>
          ))}
        </div>

        <div className="poids-row">
          <input
            ref={poidsRef}
            type="number"
            className="poids-input"
            value={poids}
            onChange={e => setPoids(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Poids en grammes"
            step="0.1"
            min="0"
          />
          <button className="btn btn-primary btn-lg" onClick={ajouterPaquet} disabled={ajoutLoading}>
            {ajoutLoading ? 'Ajout...' : 'Ajouter'}
          </button>
        </div>

        {succes && <div className="succes-msg">{succes}</div>}
        {erreur && <div className="erreur-msg">{erreur}</div>}
      </div>

      {/* Stats */}
      <div className="stats-row">
        {TYPES.map(t => (
          <StatBadge
            key={t}
            label={TYPE_LABELS[t]}
            value={stats[t]}
            color={TYPE_COLORS[t]}
          />
        ))}
        <StatBadge label="Total" value={paquets.length} color="var(--muted)" />
      </div>

      {/* Liste des paquets */}
      {paquets.length > 0 && (
        <div className="card">
          <table className="esc-table">
            <thead>
              <tr>
                <th>Type</th>
                <th>Numéro</th>
                <th>Poids (g)</th>
                {batch.prix_kg > 0 && <th>Coût</th>}
                <th>Groupe</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {paquetsAffiches.map(p => (
                <tr key={p.id}>
                  <td>
                    <span className="badge" style={{
                      background: `color-mix(in srgb, ${TYPE_COLORS[p.type_pack]} 14%, transparent)`,
                      color: TYPE_COLORS[p.type_pack]
                    }}>
                      {TYPE_LABELS[p.type_pack]}
                    </span>
                  </td>
                  <td className="font-medium">{p.numero}</td>
                  <td>{p.poids_g}g</td>
                  {batch.prix_kg > 0 && (
                    <td className="muted">{((p.poids_g / 1000) * batch.prix_kg).toFixed(2)}$</td>
                  )}
                  <td>{p.groupe ? `Groupe ${p.groupe}` : <span className="muted">—</span>}</td>
                  <td>
                    <button
                      className="btn-icon danger"
                      onClick={() => supprimerPaquet(p.id)}
                      title="Supprimer"
                    ><Icon name="supprimer" size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="etape-actions">
        <button
          className="btn btn-primary btn-lg"
          onClick={onSuivant}
          disabled={paquets.length === 0}
        >
          Créer les groupes →
        </button>
      </div>
    </div>
  );
}

// ─── ÉTAPE 3 : GROUPES ───────────────────────

function EtapeGroupes({ batch, setBatch, onSuivant }) {
  const [target, setTarget] = useState(950);
  const [max, setMax]       = useState(1050);
  const [resultat, setResultat] = useState(null);
  const [loading, setLoading]   = useState(false);
  const [erreur, setErreur]     = useState('');

  const exporterGroupesCSV = () => {
    if (!resultat) return;
    const lignes = ['groupe,type_pack,numero,poids_g,cout_paquet,poids_total_g,cout_groupe'];
    resultat.groupes.forEach(g => {
      const coutGroupe = batch.prix_kg > 0 ? ((g.total_g / 1000) * batch.prix_kg).toFixed(2) : '';
      g.packs.forEach(p => {
        const coutPaquet = batch.prix_kg > 0 ? ((p.poids_g / 1000) * batch.prix_kg).toFixed(2) : '';
        lignes.push(`${g.numero},${p.type_pack},${p.numero},${p.poids_g},${coutPaquet},${g.total_g},${coutGroupe}`);
      });
    });
    telechargerCSV(lignes.join('\n'), `groupes-${batch.nom.replace(/\s+/g, '-')}.csv`);
  };

  const exporterLeftoversCSV = () => {
    if (!resultat) return;
    const lignes = ['type_pack,numero,poids_g'];
    resultat.leftovers.forEach(p => {
      lignes.push(`${p.type_pack},${p.numero},${p.poids_g}`);
    });
    telechargerCSV(lignes.join('\n'), `leftovers-${batch.nom.replace(/\s+/g, '-')}.csv`);
  };

  const telechargerCSV = (contenu, nomFichier) => {
    const blob = new Blob([contenu], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = nomFichier;
    a.click();
    URL.revokeObjectURL(url);
  };

  const lancerGroupement = async () => {
    setLoading(true);
    setErreur('');
    try {
      const res = await api.post(`/saumon/batches/${batch.id}/grouper`, { target, max });
      setResultat(res);
      const updated = await api.get(`/saumon/batches/${batch.id}`);
      setBatch(updated);
    } catch (e) {
      setErreur(e.response?.data?.error || 'Erreur groupement');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="etape-content">
      <div className="etape-header">
        <h2>Groupement automatique</h2>
        <p className="muted">
          Batch : <strong className="gold">{batch.nom}</strong>
          · {(batch.paquets || []).length} paquets
        </p>
      </div>

      <div className="card form-card">
        <div className="form-grid">
          <div className="form-field">
            <label>Poids cible (g)</label>
            <input
              type="number"
              value={target}
              onChange={e => setTarget(parseInt(e.target.value))}
            />
          </div>
          <div className="form-field">
            <label>Poids maximum (g)</label>
            <input
              type="number"
              value={max}
              onChange={e => setMax(parseInt(e.target.value))}
            />
          </div>
        </div>
        {erreur && <p className="erreur-msg">{erreur}</p>}
        <button className="btn btn-primary btn-lg" onClick={lancerGroupement} disabled={loading}>
          {loading ? 'Calcul en cours...' : <><Icon name="auto" size={15} /> Lancer le groupement</>}
        </button>
      </div>

      {resultat && (
        <>
          {/* Résumé */}
          <div className="stats-row">
            <StatBadge label="Groupes" value={resultat.stats.nb_groupes} color="var(--gold-500)" />
            <StatBadge label="Leftovers" value={resultat.stats.nb_leftovers} color="var(--danger)" />
            <StatBadge label="Paquets" value={resultat.stats.nb_paquets} color="var(--muted)" />
          </div>

          {/* Groupes */}
          <div className="groupes-grid">
            {resultat.groupes.map(g => (
              <div key={g.numero} className="groupe-card card">
                <div className="groupe-header">
                  <span className="groupe-num gold">Groupe {g.numero}</span>
                  <span className="groupe-poids">{g.total_g}g</span>
                </div>
                <table className="esc-table groupe-table">
                  <tbody>
                    {g.packs.map(p => (
                      <tr key={p.id}>
                        <td>
                          <span className="badge" style={{
                            background: `color-mix(in srgb, ${TYPE_COLORS[p.type_pack]} 14%, transparent)`,
                            color: TYPE_COLORS[p.type_pack],
                            fontSize: 11
                          }}>
                            {p.type_pack[0]}
                          </span>
                        </td>
                        <td className="font-medium">{p.numero}</td>
                        <td className="muted">{p.poids_g}g</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>

          {/* Leftovers */}
          {resultat.leftovers.length > 0 && (
            <div className="card">
              <h3 style={{ marginBottom: 12, color: 'var(--danger)', fontSize: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
                <Icon name="attention" size={15} /> Leftovers ({resultat.leftovers.length})
              </h3>
              <div className="leftovers-list">
                {resultat.leftovers.map(p => (
                  <span key={p.id} className="leftover-tag">
                    {p.numero} — {p.poids_g}g
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="etape-actions">
            <button className="btn btn-ghost" onClick={lancerGroupement}>
              ↺ Recalculer
            </button>
            <button
              className="btn btn-ghost"
              onClick={exporterGroupesCSV}
              title="Exporter les groupes en CSV"
            >
              ↓ Groupes CSV
            </button>
            <button
              className="btn btn-ghost"
              onClick={exporterLeftoversCSV}
              disabled={resultat.leftovers.length === 0}
              title="Exporter les leftovers en CSV"
            >
              ↓ Leftovers CSV
            </button>
            <button className="btn btn-primary btn-lg" onClick={onSuivant}>
              Finaliser le batch →
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ─── MODAL DE CONFIRMATION ───────────────────

function ModalFinalisation({ batch, resultat, onFermer, onRetour }) {
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailSucces, setEmailSucces]   = useState('');
  const [emailErreur, setEmailErreur]   = useState('');

  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfErreur, setPdfErreur]   = useState('');

  const telechargerPDF = async () => {
    setPdfLoading(true);
    setPdfErreur('');
    try {
      const blob = await api.blob(`/saumon/batches/${batch.id}/pdf`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `rapport-saumon-${batch.nom.replace(/\s+/g, '-')}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      setPdfErreur('Erreur téléchargement PDF');
    } finally {
      setPdfLoading(false);
    }
  };

  const envoyerEmail = async () => {
    setEmailLoading(true);
    setEmailErreur('');
    setEmailSucces('');
    try {
      const res = await api.post(`/saumon/batches/${batch.id}/email`, {});
      setEmailSucces(`Courriel envoyé à ${res.envoye_a}`);
    } catch (e) {
      setEmailErreur(e.response?.data?.error || 'Erreur envoi courriel');
    } finally {
      setEmailLoading(false);
    }
  };

  const etapes = [
    { label: 'Batch marqué comme finalisé', ok: true },
    { label: `${resultat.nb_produits} produit(s) créés dans l'inventaire`, ok: resultat.nb_produits > 0 },
    { label: 'Écriture comptable enregistrée (coût matières)', ok: true },
  ];

  return (
    <div className="modal-overlay" onClick={onFermer}>
      <div className="modal-finalisation" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-success-icon"><Icon name="succes" size={28} /></div>
          <h2>Batch finalisé !</h2>
          <p className="muted">{batch.nom}</p>
        </div>

        <ul className="modal-checklist">
          {etapes.map((e, i) => (
            <li key={i} className={`checklist-item ${e.ok ? 'ok' : 'warn'}`}>
              <span className="checklist-icon">
                {e.ok ? <Icon name="check" size={14} weight="bold" /> : <Icon name="attention" size={14} />}
              </span>
              {e.label}
            </li>
          ))}
        </ul>

        <div className="modal-actions">
          <button className="btn btn-ghost" onClick={telechargerPDF} disabled={pdfLoading}>
            <Icon name="telecharger" size={14} />{pdfLoading ? ' Génération...' : ' Télécharger PDF'}
          </button>
          <button className="btn btn-ghost" onClick={envoyerEmail} disabled={emailLoading}>
            <Icon name="email" size={14} />{emailLoading ? ' Envoi...' : ' Envoyer par courriel'}
          </button>
        </div>

        {pdfErreur  && <p className="erreur-msg" style={{ textAlign: 'center', marginTop: 10 }}>{pdfErreur}</p>}
        {emailSucces && <p className="succes-msg" style={{ textAlign: 'center', marginTop: 10 }}>{emailSucces}</p>}
        {emailErreur && <p className="erreur-msg" style={{ textAlign: 'center', marginTop: 10 }}>{emailErreur}</p>}

        {resultat.deductions && resultat.deductions.length > 0 && (
          <div style={{ marginTop: 16, textAlign: 'left' }}>
            <h4 style={{ marginBottom: 10 }}>Inventaire déduit</h4>
            <table className="esc-table">
              <thead>
                <tr><th>Ingrédient</th><th>Déduit</th><th>Stock restant</th></tr>
              </thead>
              <tbody>
                {resultat.deductions.map(d => (
                  <tr key={d.matiere_id}>
                    <td>{d.nom}</td>
                    <td className="gold">−{d.quantite_deduite} {d.unite}</td>
                    <td className={d.statut === 'negatif' ? 'danger' : d.statut === 'bas' ? 'warning' : 'success'}>
                      {d.stock_apres} {d.unite}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {resultat.deductions.some(d => d.statut !== 'ok') && (
              <p className="warning" style={{ marginTop: 8, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
                <Icon name="attention" size={14} /> {resultat.deductions.filter(d => d.statut !== 'ok').length} ingrédient(s) à réapprovisionner
              </p>
            )}
          </div>
        )}

        <CoutRevient analytique={resultat.analytique} />

        <div className="modal-footer">
          <button className="btn btn-primary btn-lg" onClick={onRetour}>
            ← Retour aux batches
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── ÉTAPE 4 : FINALISER ─────────────────────

function EtapeFinaliser({ batch, onFinalise }) {
  const isFinalise = batch.statut === 'finalise';
  const [prixKg, setPrixKg] = useState(batch.prix_kg || '');
  const [modePrixVente, setModePrixVente] = useState(batch.mode_prix_vente || 'kg');
  const [prixVenteKg, setPrixVenteKg] = useState(batch.prix_vente_kg || '');
  const [prixVenteUnite, setPrixVenteUnite] = useState(batch.prix_vente_unite || '');
  const [recalcLoading, setRecalcLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [erreur, setErreur]   = useState('');
  const [resultat, setResultat] = useState(null);
  const [apercuDeduction, setApercuDeduction] = useState(null);
  const [finalForm, setFinalForm] = useState(() => {
    const defaut = creerFormulaireTraiteFinal('saumon');
    return {
      ...defaut,
      date_congelation: batch.date_congelation || defaut.date_congelation,
      date_expiration_produit: batch.date_expiration_produit || defaut.date_expiration_produit,
      temp_conservation: batch.temp_conservation || defaut.temp_conservation,
      incident: batch.incident || '',
    };
  });

  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfErreur, setPdfErreur]   = useState('');
  const [emailLoading2, setEmailLoading2] = useState(false);
  const [emailSucces2, setEmailSucces2]   = useState('');
  const [emailErreur2, setEmailErreur2]   = useState('');

  useEffect(() => {
    if (batch.statut !== 'finalise') {
      api.get(`/saumon/batches/${batch.id}/apercu-deduction`)
        .then(setApercuDeduction)
        .catch(() => {});
    }
  }, []); // eslint-disable-line

  const telechargerPDFDirect = async () => {
    setPdfLoading(true);
    setPdfErreur('');
    try {
      const blob = await api.blob(`/saumon/batches/${batch.id}/pdf`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `rapport-saumon-${batch.nom.replace(/\s+/g, '-')}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      setPdfErreur('Erreur téléchargement PDF');
    } finally {
      setPdfLoading(false);
    }
  };

  const envoyerEmailDirect = async () => {
    setEmailLoading2(true);
    setEmailErreur2('');
    setEmailSucces2('');
    try {
      const res = await api.post(`/saumon/batches/${batch.id}/email`, {});
      setEmailSucces2(`Courriel envoyé à ${res.envoye_a}`);
    } catch (e) {
      setEmailErreur2(e.response?.data?.error || 'Erreur envoi courriel');
    } finally {
      setEmailLoading2(false);
    }
  };

  const recalculerPrix = async () => {
    setRecalcLoading(true);
    setErreur('');
    try {
      await api.post(`/saumon/batches/${batch.id}/recalculer-prix-ventes`);
    } catch (e) {
      setErreur(e.response?.data?.error || 'Erreur lors du recalcul');
    } finally {
      setRecalcLoading(false);
    }
  };

  const paquets   = batch.paquets || [];
  const groupesNums = [...new Set(paquets.filter(p => p.groupe).map(p => p.groupe))];
  const leftovers  = paquets.filter(p => !p.groupe);
  const prixVenteKgNum = parseFloat(prixVenteKg) || 0;
  const prixVenteUniteNum = parseFloat(prixVenteUnite) || 0;

  // Total poids groupes pour live total
  const totalPoidsGroupesG = paquets.filter(p => p.groupe).reduce((s, p) => s + p.poids_g, 0);

  // Calculer aperçu des groupes (prix de vente, pas prix d'achat)
  const groupesDetails = groupesNums.map(num => {
    const packs = paquets.filter(p => p.groupe === num);
    const totalG = packs.reduce((s, p) => s + p.poids_g, 0);
    const prixVente = modePrixVente === 'kg'
      ? Math.round((totalG / 1000) * prixVenteKgNum * 100) / 100
      : prixVenteUniteNum;
    return {
      num,
      packs,
      totalG: Math.round(totalG * 10) / 10,
      prix: prixVente,
    };
  });

  const exporterWooCommerce = () => {
    if (groupesDetails.length === 0) return;
    const batchCode = batch.nom.replace(/\s+/g, '').toUpperCase();
    const description = (
      'Un produit rare et raffiné, offert en production limitée.\n\n' +
      'Ce saumon fumé à froid est préparé avec soin à partir de sel de Guérande, ' +
      "d'une pointe de cassonade et d'un mélange unique d'épices secrètes. " +
      'Il est fumé lentement au bois de cerisier, ce qui lui confère une saveur douce et subtile.\n\n' +
      'Produit congelé — uniquement sur commande.'
    );
    const imageUrl = 'https://escamel.ca/wp-content/uploads/2025/04/IMG_3945-1-scaled.jpg';

    const headers = [
      'Name', 'Type', 'Published', 'Regular price', 'Categories',
      'SKU', 'Weight (kg)', 'Short description', 'Description',
      'Stock status', 'Stock', 'Sold individually?', 'Images', 'Catalog visibility'
    ];

    const echapper = (val) => {
      const s = String(val ?? '');
      return s.includes(',') || s.includes('"') || s.includes('\n')
        ? `"${s.replace(/"/g, '""')}"` : s;
    };

    const lignes = [headers.join(',')];
    groupesDetails.forEach(g => {
      const packLines = g.packs
        .map(p => `• ${p.numero} — ${p.poids_g} g`)
        .join('\n');
      const shortDesc =
        `Inclus dans ce lot :\n${packLines}\n\nPoids total : ${(g.totalG / 1000).toFixed(3)} kg\nProduit congelé — sur commande seulement.`;
      const sku = `SAUFUM-${batchCode}-G${g.num}`;
      const row = [
        `Saumon Fumé — Groupe ${g.num} (${batch.nom})`,
        'simple', '1',
        g.prix > 0 ? g.prix : '',
        'Saumon Fumé', sku,
        (g.totalG / 1000).toFixed(3),
        shortDesc, description,
        'instock', '1', '1', imageUrl, 'visible'
      ].map(echapper);
      lignes.push(row.join(','));
    });

    const blob = new Blob([lignes.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `woocommerce_import-${batch.nom.replace(/\s+/g, '-')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const finaliser = async () => {
    if (groupesNums.length === 0) {
      setErreur("Aucun groupe créé — retourne à l'étape précédente");
      return;
    }
    setLoading(true);
    setErreur('');
    try {
      const res = await api.post(`/saumon/batches/${batch.id}/finaliser`, {
        prix_kg: parseFloat(prixKg),
        ...finalForm,
      });
      setResultat(res);
    } catch (e) {
      setErreur(e.response?.data?.error || 'Erreur finalisation');
    } finally {
      setLoading(false);
    }
  };

  if (resultat) {
    return (
      <div className="etape-content">
        <ModalFinalisation
          batch={batch}
          resultat={resultat}
          onFermer={onFinalise}
          onRetour={onFinalise}
        />
      </div>
    );
  }

  return (
    <div className="etape-content">
      <div className="etape-header">
        <h2>Finaliser le batch</h2>
        <p className="muted">
          Batch : <strong className="gold">{batch.nom}</strong>
          · {groupesNums.length} groupes · {leftovers.length} leftovers
        </p>
      </div>

      <div className="card form-card">
        {/* BLOC ACHAT */}
        <div style={{
          background: 'rgba(255,255,255,0.03)',
          border: '1px solid var(--border)',
          borderRadius: 8,
          padding: '12px 14px',
          marginBottom: 12,
        }}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 10, color: 'var(--muted)' }}>
            Prix d'achat matière (cru)
          </div>
          <div className="form-field" style={{ maxWidth: 200 }}>
            <label>
              Prix d'achat matière (cru) — $/kg
              {isFinalise && (
                <span title="Finalisé — non modifiable (impact comptable/stock)" style={{ marginLeft: 6, fontSize: 11, color: 'var(--muted)', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                  <Icon name="verrou" size={12} /> Verrouillé
                </span>
              )}
            </label>
            <input
              type="number"
              step="0.01"
              value={prixKg}
              onChange={e => setPrixKg(e.target.value)}
              disabled={isFinalise}
              placeholder="ex: 18.50"
              style={{ opacity: isFinalise ? 0.5 : 1 }}
            />
          </div>
        </div>

        {/* BLOC VENTE — mode par groupe (toggle ici = défaut batch) */}
        <BlocPrixVente
          mode={modePrixVente}
          prixKg={prixVenteKg}
          prixUnite={prixVenteUnite}
          onModeChange={setModePrixVente}
          onPrixKgChange={setPrixVenteKg}
          onPrixUniteChange={setPrixVenteUnite}
          hasToggle={true}
          verrouille={isFinalise}
          poidsTotal={totalPoidsGroupesG}
          labelUnite="Prix de vente à l'unité ($/groupe)"
        />

        {/* Bouton recalculer (pré-finalisation seulement) */}
        {!isFinalise && groupesNums.length > 0 && (
          <div style={{ marginTop: 10 }}>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={recalculerPrix}
              disabled={recalcLoading}
            >
              {recalcLoading ? 'Recalcul...' : '↺ Recalculer les prix gravés'}
            </button>
          </div>
        )}
      </div>

      {/* Aperçu des produits */}
      <SectionProduitFini
        produit="saumon"
        form={finalForm}
        setForm={setFinalForm}
        batchId={batch.id}
        batchStatut={batch.statut}
      />

      {groupesDetails.length > 0 && (
        <div className="card">
          <h3 style={{ marginBottom: 14, fontSize: 14, color: 'var(--text)' }}>
            Aperçu des produits à créer
          </h3>
          <table className="esc-table">
            <thead>
              <tr>
                <th>SKU</th>
                <th>Paquets</th>
                <th>Poids total</th>
                <th>Prix de vente</th>
              </tr>
            </thead>
            <tbody>
              {groupesDetails.map(g => {
                const batchCode = batch.nom.replace(/\s+/g, '').toUpperCase();
                return (
                  <tr key={g.num}>
                    <td className="font-medium gold">SAUFUM-{batchCode}-G{g.num}</td>
                    <td className="muted">{g.packs.length} paquets</td>
                    <td>{g.totalG}g ({(g.totalG / 1000).toFixed(3)}kg)</td>
                    <td className="success">{g.prix > 0 ? `${g.prix}$` : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {erreur && <p className="erreur-msg">{erreur}</p>}

      {apercuDeduction && apercuDeduction.deductions && apercuDeduction.deductions.length > 0 && (
        <div className="card" style={{ marginTop: 16 }}>
          <h4 style={{ marginBottom: 10 }}>Aperçu — Déduction inventaire</h4>
          <p className="muted" style={{ fontSize: 13, marginBottom: 12 }}>
            Ces quantités seront déduites de l'inventaire à la finalisation.
          </p>
          <table className="esc-table">
            <thead>
              <tr><th>Ingrédient</th><th>Déduit</th><th>Stock avant</th><th>Stock après</th></tr>
            </thead>
            <tbody>
              {apercuDeduction.deductions.map(d => (
                <tr key={d.matiere_id}>
                  <td>{d.nom}</td>
                  <td className="gold">−{d.quantite_deduite} {d.unite}</td>
                  <td className="muted">{d.stock_avant} {d.unite}</td>
                  <td className={d.statut === 'negatif' ? 'danger' : d.statut === 'bas' ? 'warning' : 'success'}>
                    {d.stock_apres} {d.unite}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {apercuDeduction.deductions.some(d => d.statut !== 'ok') && (
            <p className="warning" style={{ marginTop: 8, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
              <Icon name="attention" size={14} /> {apercuDeduction.deductions.filter(d => d.statut !== 'ok').length} ingrédient(s) passeront sous le seuil après déduction.
            </p>
          )}
        </div>
      )}

      {(pdfErreur || emailErreur2 || emailSucces2) && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 8 }}>
          {pdfErreur   && <p className="erreur-msg">{pdfErreur}</p>}
          {emailErreur2 && <p className="erreur-msg">{emailErreur2}</p>}
          {emailSucces2 && <p className="succes-msg">{emailSucces2}</p>}
        </div>
      )}

      <div className="etape-actions">
        <button
          className="btn btn-ghost"
          onClick={exporterWooCommerce}
          disabled={groupesDetails.length === 0}
          title="Générer le fichier CSV d'import WooCommerce"
        >
          ↓ WooCommerce CSV
        </button>
        <button
          className="btn btn-ghost"
          onClick={telechargerPDFDirect}
          disabled={pdfLoading || groupesNums.length === 0}
          title="Télécharger le rapport PDF"
        >
          <Icon name="telecharger" size={14} />{pdfLoading ? ' Génération...' : ' Télécharger PDF'}
        </button>
        <button
          className="btn btn-ghost"
          onClick={envoyerEmailDirect}
          disabled={emailLoading2 || groupesNums.length === 0}
          title="Envoyer le rapport par courriel"
        >
          <Icon name="email" size={14} />{emailLoading2 ? ' Envoi...' : ' Envoyer par courriel'}
        </button>
        <button
          className="btn btn-primary btn-lg"
          onClick={finaliser}
          disabled={loading || groupesNums.length === 0}
        >
          {loading ? 'Finalisation...' : <><Icon name="check" size={15} weight="bold" /> Finaliser et créer les produits</>}
        </button>
      </div>
    </div>
  );
}

// ─── COMPOSANT PRINCIPAL ─────────────────────

export default function ProductionSaumon() {
  const [etape, setEtape] = useState(1);
  const [batch, setBatch] = useState(null);
  const [nbTracabiliteNonLies, setNbTracabiliteNonLies] = useState(0);
  const isFinalise = batch?.statut === 'finalise';

  const batchCree = (b) => {
    setBatch(b);
    setEtape(b.statut === 'finalise' ? 6 : 2);
  };

  const reset = () => {
    setBatch(null);
    setEtape(1);
  };

  const handleStepClick = (cible) => {
    // Ne pas réinitialiser le batch quand on clique sur l'étape 1.
    // L'étape 1 avec un batch ouvert affiche la liste + le batch courant sélectionné.
    if (!batch && cible > 1) return;
    if (cible < etape) { setEtape(cible); return; }
    if (isFinalise) { setEtape(cible); return; }
    if (cible <= etape + 1) setEtape(cible);
  };

  return (
    <div className="production-saumon">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Production — Saumon Fumé</h1>
          <p className="muted page-subtitle">Gestion des batches et groupement des paquets</p>
        </div>
        {batch && (
          <button className="btn btn-ghost" onClick={reset}>
            + Nouveau batch / Changer
          </button>
        )}
      </div>

      {/* Stepper */}
      <Stepper
        etape={etape}
        nbTracabiliteNonLies={nbTracabiliteNonLies}
        onStepClick={handleStepClick}
        batch={batch}
        isFinalise={isFinalise}
      />

      {/* Contenu par étape */}
      {etape === 1 && <EtapeBatch onBatchCree={batchCree} />}
      {etape === 2 && batch && (
        <EtapeTracabilite
          batchId={batch?.id}
          batchNom={batch?.nom}
          batchStatut={batch?.statut}
          onNext={() => setEtape(3)}
          onBack={() => setEtape(1)}
          onNbNonLiesChange={setNbTracabiliteNonLies}
        />
      )}
      {etape === 3 && batch && (
        <EtapeRecette
          batch={batch}
          setBatch={setBatch}
          onSuivant={() => setEtape(4)}
        />
      )}
      {etape === 4 && batch && (
        <EtapeSaisie
          batch={batch}
          setBatch={setBatch}
          onSuivant={() => setEtape(5)}
        />
      )}
      {etape === 5 && batch && (
        <EtapeGroupes
          batch={batch}
          setBatch={setBatch}
          onSuivant={() => setEtape(6)}
        />
      )}
      {etape === 6 && batch && (
        <EtapeFinaliser
          batch={batch}
          onFinalise={reset}
        />
      )}
    </div>
  );
}
